import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "./Reveal";
import { Calendar, Images, X, ChevronLeft, ChevronRight, Facebook, MessageCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const fullUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);
const getImages = (item) => (item.images && item.images.length ? item.images : item.image_url ? [item.image_url] : []).map(fullUrl);

const fmtDate = (d, lang) => {
  try {
    return new Date(d).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
};

function Lightbox({ images, index, setIndex, onClose }) {
  const prev = useCallback((e) => { e?.stopPropagation(); setIndex((i) => (i - 1 + images.length) % images.length); }, [images.length, setIndex]);
  const next = useCallback((e) => { e?.stopPropagation(); setIndex((i) => (i + 1) % images.length); }, [images.length, setIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  return (
    <motion.div
      data-testid="lightbox"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-foreground/95 backdrop-blur-sm flex flex-col items-center justify-center p-4"
    >
      <button data-testid="lightbox-close" onClick={onClose} className="absolute top-5 right-5 grid place-items-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
        <X size={20} />
      </button>
      <motion.img
        key={index}
        initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
        src={images[index]} alt="" onClick={(e) => e.stopPropagation()}
        className="max-h-[80vh] max-w-[92vw] object-contain rounded-xl"
      />
      {images.length > 1 && (
        <>
          <button data-testid="lightbox-prev" onClick={prev} className="absolute left-3 md:left-8 grid place-items-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><ChevronLeft size={22} /></button>
          <button data-testid="lightbox-next" onClick={next} className="absolute right-3 md:right-8 grid place-items-center w-11 h-11 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><ChevronRight size={22} /></button>
          <div className="mt-4 flex gap-2" onClick={(e) => e.stopPropagation()}>
            {images.map((im, i) => (
              <button key={i} onClick={() => setIndex(i)} className={`h-14 w-14 rounded-lg overflow-hidden border-2 transition-colors ${i === index ? "border-accent" : "border-transparent opacity-60 hover:opacity-100"}`}>
                <img src={im} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
}

export const News = () => {
  const { t, lang } = useLang();
  const n = t.news;
  const [items, setItems] = useState([]);
  const [box, setBox] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/news`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const openBox = (images, index) => setBox({ images, index });
  const cats = Array.from(new Set(items.map((i) => i.categoria).filter(Boolean)));
  const shown = filter === "all" ? items : items.filter((i) => i.categoria === filter);

  return (
    <section id="news" className="py-24 md:py-32 bg-secondary relative z-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="max-w-2xl mb-10">
          <Reveal><span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary mb-5">{n.tag}</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02]">{n.title}</h2>
          </Reveal>
        </div>

        {items.length > 0 && cats.length > 1 && (
          <div data-testid="news-filters" className="flex flex-wrap gap-2 mb-12">
            {["all", ...cats].map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setFilter(c)}
                className={`text-sm font-semibold rounded-full px-4 py-2 transition-colors ${filter === c ? "bg-primary text-primary-foreground" : "border border-foreground/15 hover:bg-white"}`}
              >
                {c === "all" ? n.filterAll : c}
              </button>
            ))}
          </div>
        )}

        {items.length === 0 ? (
          <Reveal><p className="text-muted-foreground text-lg">{n.empty}</p></Reveal>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shown.map((item, i) => {
              const title = lang === "en" && item.titolo_en ? item.titolo_en : item.titolo_it;
              const text = lang === "en" && item.testo_en ? item.testo_en : item.testo_it;
              const imgs = getImages(item);
              const shareUrl = `${window.location.origin}/#news`;
              return (
                <Reveal key={item.id} delay={(i % 3) * 0.08}>
                  <article data-testid={`news-card-${i}`} className="group h-full rounded-2xl overflow-hidden bg-surface border border-black/5 hover:border-primary/30 transition-all flex flex-col">
                    {imgs.length > 0 && (
                      <button onClick={() => openBox(imgs, 0)} className="relative aspect-[16/10] overflow-hidden block text-left">
                        <img src={imgs[0]} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        {imgs.length > 1 && (
                          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-black/60 text-white px-2.5 py-1 rounded-full">
                            <Images size={13} /> {imgs.length}
                          </span>
                        )}
                      </button>
                    )}
                    <div className="p-7 flex flex-col flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-xs font-semibold uppercase tracking-wider bg-accent/15 text-[hsl(26,90%,40%)] px-2.5 py-1 rounded-full">{item.categoria}</span>
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Calendar size={13} />{fmtDate(item.data, lang)}</span>
                      </div>
                      <h3 className="font-display font-bold text-xl tracking-tight mb-2">{title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{text}</p>
                      <div className="mt-auto pt-5 flex items-center gap-2">
                        <span className="text-xs text-muted-foreground mr-1">{n.share}</span>
                        <button
                          data-testid={`share-fb-${i}`}
                          onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "width=640,height=560")}
                          aria-label="Facebook"
                          className="grid place-items-center w-8 h-8 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                        >
                          <Facebook size={15} />
                        </button>
                        <a
                          data-testid={`share-wa-${i}`}
                          href={`https://wa.me/?text=${encodeURIComponent(`${title} — ${shareUrl}`)}`}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="WhatsApp"
                          className="grid place-items-center w-8 h-8 rounded-full bg-[#25D366]/15 text-[#128C4B] hover:bg-[#25D366] hover:text-white transition-colors"
                        >
                          <MessageCircle size={15} />
                        </a>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>

      {createPortal(
        <AnimatePresence>
          {box && <Lightbox images={box.images} index={box.index} setIndex={(fn) => setBox((b) => ({ ...b, index: typeof fn === "function" ? fn(b.index) : fn }))} onClose={() => setBox(null)} />}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
};
